import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Users, Building2, Truck, Box, UserCircle, Check, X } from 'lucide-react';
import { API_URL, ENTITY_CONFIG } from '../utils/constants';

const AssignAdminModal = ({ isOpen, onClose, entityType, entities, admins, companies, onSuccess }) => {
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [assigning, setAssigning] = useState(false);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedEntities([]);
      setSelectedAdmins([]);
    }
  }, [isOpen]);

  const config = ENTITY_CONFIG[entityType];
  
  // Early return después de los hooks
  if (!config) return null;

  const getEntityIcon = () => {
    switch (entityType) {
      case 'companies': return <Building2 size={16} />;
      case 'trucks': return <Truck size={16} />;
      case 'trailers': return <Box size={16} />;
      case 'drivers': return <UserCircle size={16} />;
      default: return null;
    }
  };


  const getEntityLabel = (entity) => {
    switch (entityType) {
      case 'companies': return entity.name;
      case 'trucks': return `${entity.license_plate} - ${companies.find(c => c.id === entity.company_id)?.name || ''}`;
      case 'trailers': return `${entity.license_plate} - ${companies.find(c => c.id === entity.company_id)?.name || ''}`;
      case 'drivers': return `${entity.name} - ${companies.find(c => c.id === entity.company_id)?.name || ''}`;
      default: return entity.id;
    }
  };

  const toggleEntity = (entityId) => {
    setSelectedEntities(prev => 
      prev.includes(entityId) 
        ? prev.filter(id => id !== entityId)
        : [...prev, entityId]
    );
  };

  const toggleAdmin = (adminId) => {
    setSelectedAdmins(prev => 
      prev.includes(adminId) 
        ? prev.filter(id => id !== adminId)
        : [...prev, adminId]
    );
  };

  const selectAllEntities = () => {
    if (selectedEntities.length === entities.length) {
      setSelectedEntities([]);
    } else {
      setSelectedEntities(entities.map(e => e.id));
    }
  };

  const handleAssign = async () => {
    if (selectedEntities.length === 0) {
      toast.error(`Selecciona al menos un ${config.singular.toLowerCase()}`);
      return;
    }
    if (selectedAdmins.length === 0) {
      toast.error('Selecciona al menos un administrador');
      return;
    }

    setAssigning(true);
    
    try {
      // Endpoint para asignación masiva
      await axios.post(`${API_URL}/assignments/bulk`, {
        entity_type: entityType,
        entity_ids: selectedEntities,
        admin_ids: selectedAdmins
      });
      
      toast.success(`${selectedEntities.length} ${config.label.toLowerCase()} asignados a ${selectedAdmins.length} administrador(es)`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={20} />
            Asignar {config.label} a Administradores
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Lista de entidades */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-slate-700 flex items-center gap-2">
                {getEntityIcon()}
                {config.label} ({entities.length})
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={selectAllEntities}
                className="text-xs"
              >
                {selectedEntities.length === entities.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </Button>
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-1">
              {entities.map(entity => (
                <div 
                  key={entity.id}
                  onClick={() => toggleEntity(entity.id)}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    selectedEntities.includes(entity.id) 
                      ? 'bg-violet-100 border border-violet-300' 
                      : 'hover:bg-slate-100'
                  }`}
                >
                  <Checkbox 
                    checked={selectedEntities.includes(entity.id)}
                    onCheckedChange={() => toggleEntity(entity.id)}
                  />
                  <span className="text-sm text-slate-700 truncate">
                    {getEntityLabel(entity)}
                  </span>
                </div>
              ))}
              {entities.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">
                  No hay {config.label.toLowerCase()} disponibles
                </p>
              )}
            </div>

            <div className="mt-2 pt-2 border-t text-xs text-slate-500">
              {selectedEntities.length} seleccionados
            </div>
          </div>

          {/* Lista de administradores */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium text-slate-700 flex items-center gap-2 mb-3">
              <Users size={16} />
              Administradores ({admins.length})
            </h3>
            
            <div className="max-h-64 overflow-y-auto space-y-1">
              {admins.map(admin => (
                <div 
                  key={admin.id}
                  onClick={() => toggleAdmin(admin.id)}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    selectedAdmins.includes(admin.id) 
                      ? 'bg-emerald-100 border border-emerald-300' 
                      : 'hover:bg-slate-100'
                  }`}
                >
                  <Checkbox 
                    checked={selectedAdmins.includes(admin.id)}
                    onCheckedChange={() => toggleAdmin(admin.id)}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-700">{admin.full_name}</span>
                    <span className="text-xs text-slate-500">{admin.email}</span>
                  </div>
                </div>
              ))}
              {admins.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">
                  No hay administradores disponibles
                </p>
              )}
            </div>

            <div className="mt-2 pt-2 border-t text-xs text-slate-500">
              {selectedAdmins.length} seleccionados
            </div>
          </div>
        </div>

        {/* Resumen */}
        {selectedEntities.length > 0 && selectedAdmins.length > 0 && (
          <div className="bg-violet-50 rounded-lg p-3 border border-violet-200">
            <p className="text-sm text-violet-700">
              <strong>{selectedEntities.length}</strong> {config.label.toLowerCase()} serán asignados a{' '}
              <strong>{selectedAdmins.length}</strong> administrador(es)
            </p>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>
            <X size={16} className="mr-1" /> Cancelar
          </Button>
          <Button 
            onClick={handleAssign}
            disabled={selectedEntities.length === 0 || selectedAdmins.length === 0 || assigning}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {assigning ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Asignando...
              </>
            ) : (
              <>
                <Check size={16} className="mr-1" /> Asignar
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignAdminModal;
